import type { Story, StoryGroup } from "Components/Stories/StoriesSection";

/**
 * Story media warming — plain GETs against our media endpoint (immutably cached), so the only
 * budget that matters here is the user's data plan: entry stories only, video metadata only,
 * nothing on Save-Data.
 */

/** How many stories to warm per circle: the entry story and the one right after it. */
const PREFETCHED_STORIES_PER_GROUP = 2;

/** setTimeout stand-in delay where requestIdleCallback is unavailable (Safari). */
const IDLE_FALLBACK_DELAY_MS = 200;

const prefetchedUrls = new Set<string>();
/** Keeps detached preload <video> elements alive until their metadata arrives. */
const pendingVideoPreloads = new Set<HTMLVideoElement>();

type NavigatorWithConnection = Navigator & {
  connection?: { saveData?: boolean };
};

const isPrefetchAllowed = (): boolean =>
  (navigator as NavigatorWithConnection).connection?.saveData !== true;

const prefetchImage = (url: string): void => {
  new Image().src = url;
};

const prefetchVideoMetadata = (url: string): void => {
  const video = document.createElement("video");
  const release = () => {
    video.removeAttribute("src");
    pendingVideoPreloads.delete(video);
  };
  video.preload = "metadata";
  video.addEventListener("loadedmetadata", release, { once: true });
  video.addEventListener("error", release, { once: true });
  video.src = url;
  pendingVideoPreloads.add(video);
};

const prefetchUrl = (
  url: string | undefined,
  prefetch: (url: string) => void,
): void => {
  if (!url || prefetchedUrls.has(url)) {
    return;
  }
  prefetchedUrls.add(url);
  prefetch(url);
};

/** Warms one story's media: the image; for videos only metadata + the poster. */
export const prefetchStoryMedia = (story: Story | undefined): void => {
  if (!story || !isPrefetchAllowed()) {
    return;
  }
  if (story.videoUrl) {
    prefetchUrl(story.videoUrl, prefetchVideoMetadata);
  }
  if (story.imageUrl) {
    prefetchUrl(story.imageUrl, prefetchImage);
  }
};

/** The story the viewer opens at: first unread, else the first (all-read sources replay). */
export const getEntryStoryIndex = (group: StoryGroup): number =>
  Math.max(
    0,
    group.stories.findIndex((story) => !story.isRead),
  );

/** Warms the stories a tap on this circle would play first. */
export const prefetchGroupEntry = (group: StoryGroup | undefined): void => {
  if (!group) {
    return;
  }
  const entryIndex = getEntryStoryIndex(group);
  for (let offset = 0; offset < PREFETCHED_STORIES_PER_GROUP; offset += 1) {
    prefetchStoryMedia(group.stories[entryIndex + offset]);
  }
};

/** requestIdleCallback with a setTimeout fallback (Safari); returns the cancel function. */
export const scheduleIdlePrefetch = (callback: () => void): (() => void) => {
  if (typeof window.requestIdleCallback === "function") {
    const idleCallbackId = window.requestIdleCallback(callback);
    return () => window.cancelIdleCallback(idleCallbackId);
  }
  const timeoutId = window.setTimeout(callback, IDLE_FALLBACK_DELAY_MS);
  return () => window.clearTimeout(timeoutId);
};
